import { useEffect } from 'react';
// this is marked as unstable for now, but a stable version is coming soon (https://github.com/remix-run/react-router/issues/8139#issuecomment-1397616767)
import { useBlocker } from 'react-router-dom';

import {
  SaveChangesModalProps,
  SaveChangesModal,
} from '@/features/SaveChangesModal/SaveChangesModal';
import { isElectron } from 'src/utils/isElectron';

const useNativeBrowserNavigationBlocker = (enabled: boolean) => {
  useEffect(() => {
    if (enabled && !isElectron) {
      // eslint-disable-next-line func-names
      const onUnload = function (event: BeforeUnloadEvent) {
        // custom messages are no longer supported by browsers, so just
        // providing in a truthy value here without which it might not
        // show the native confirmation dialog
        const customMessage = '.';

        (event || window.event).returnValue = customMessage;
        return customMessage;
      };

      window.addEventListener('beforeunload', onUnload);
      return () => window.removeEventListener('beforeunload', onUnload);
    }

    return undefined;
  }, [enabled]);
};

type NavigationBlockerProps = Omit<
  SaveChangesModalProps,
  'open' | 'onReturnToEditing' | 'onDoNotSave'
> & { readonly enabled: boolean };

export const UnsavedChangesNavigationBlocker = ({
  enabled,
  onSave,
  isSaving,
}: NavigationBlockerProps) => {
  // react-router's navigation blocker
  const blocker = useBlocker(enabled);

  // add in the native browser's navigation blocker (for navigation
  // which isn't handled by react-router, e.g. page refreshes)
  useNativeBrowserNavigationBlocker(enabled);

  const modifiedOnSave = async () => {
    await onSave();
    blocker.proceed?.();
  };

  return (
    <SaveChangesModal
      open={blocker.state === 'blocked'}
      isSaving={isSaving}
      onReturnToEditing={blocker.reset as () => void}
      onDoNotSave={blocker.proceed as () => void}
      onSave={modifiedOnSave}
    />
  );
};
